<template>
  <div class="okr-reports mt-2">
    <b-card class="mb-2">
      <h5>Filtros</h5>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="5" class="mb-1">
            <b-form-group label="Período">
              <div class="d-flex align-items-center" style="gap: 0.4rem; flex-wrap: nowrap">
                <v-select
                  v-model="filters.startQuarter"
                  :options="quarterOptions"
                  :clearable="false"
                  :reduce="opt => opt.value"
                  label="label"
                  style="min-width: 68px; flex: 1"
                />
                <v-select
                  v-model="filters.startYear"
                  :options="yearOptions"
                  :clearable="false"
                  style="min-width: 80px; flex: 1"
                />
                <span class="text-muted font-small-3 px-25">até</span>
                <v-select
                  v-model="filters.endQuarter"
                  :options="quarterOptions"
                  :clearable="false"
                  :reduce="opt => opt.value"
                  label="label"
                  style="min-width: 68px; flex: 1"
                />
                <v-select
                  v-model="filters.endYear"
                  :options="yearOptions"
                  :clearable="false"
                  style="min-width: 80px; flex: 1"
                />
              </div>
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4" class="mb-1">
            <b-form-group
              label="PN"
              label-for="searchConsultant"
            >
              <v-select
                id="searchConsultant"
                multiple
                v-model="filters.consultant"
                :reduce="(consultant_list) => consultant_list.id"
                :options="consultantList"
                :loading="loading.consultantList"
                :close-on-select="false"
                label="name"
                @keyup.enter="fetchReports"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>

          <b-col cols="12" md="4" class="mb-1">
            <b-form-group label="Franqueado" label-for="searchFranchise">
              <v-select
                id="searchFranchise"
                multiple
                v-model="filters.franchises"
                :reduce="(franchise_list) => franchise_list.id"
                :options="franchises"
                :loading="loading.franchises"
                :close-on-select="false"
                label="name"
                @keyup.enter="fetchReports"
              >
                <template #no-options="{ search, searching, loading }">
                  Sem resultados
                </template>
              </v-select>
            </b-form-group>
          </b-col>
        </b-row>

        <div class="text-right mt-2">
          <b-button
            variant="outline-primary"
            @click="fetchReports"
            :disabled="loading.table"
          >
            <b-spinner v-if="loading.table" small />
            <feather-icon v-else icon="SearchIcon" />
            <span> {{ loading.table ? "Aguarde..." : "Pesquisar" }} </span>
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <div class="mb-2">
      <b-card no-body class="border-0 shadow">
        <b-card-header class="pb-50">
          <h4 class="mb-1">
            <feather-icon size="20" icon="BarChart3Icon" />
            <span>OKR</span>
          </h4>
          <b-card-text class="mb-1">
            <b-badge variant="primary" class="mr-1">
              <span>Arraste para o lado</span>
              <feather-icon icon="ChevronsRightIcon" class="ml-25" />
            </b-badge>
          </b-card-text>
        </b-card-header>
        <b-table
          :items="processedReportData"
          :fields="tableColumns"
          :busy="loading.table"
          :tbody-tr-class="rowClass"
          :sticky-header="stickyHeaderHeight"
          hover
          responsive
          no-border-collapse
          show-empty
          striped
          :class="[
            'position-relative text-nowrap mb-0',
            detectSafariBrowser ? 'pb-1 scroll-fixed' : '',
          ]"
          primary-key="month"
          empty-text="Não foram encontrados dados OKR para esta busca"
        >
          <template #thead-top="data">
            <b-tr>
              <b-th class="header-spacing"></b-th>
              <b-th colspan="3" class="header-spacing text-center bg-danger bg-lighten-2">Leads</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-danger text-white">Leads Quentes</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-info">Entrevistas Agendadas</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-info bg-lighten-3">Entrevistas Feitas</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-info bg-lighten-2">Entrevistas Pagas</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-green-2 bg-lighten-2">Faturamento de EP (R$)</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-blue bg-lighten-2 text-white">Momentos X Agendados</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-primary bg-lighten-2">Parcerias Agendadas</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-secondary bg-lighten-5">Assinaturas (R$)</b-th>
              <b-th colspan="3" class="header-spacing text-center bg-green-2 bg-lighten-5">Soluções (R$)</b-th>
              <b-th colspan="3" class="text-center bg-success bg-lighten-2">Total (R$)</b-th>
            </b-tr>
          </template>

          <!-- Column: Month -->
          <template #cell(month)="data">
            <div class="text-nowrap">
              <span class="align-text-top text-capitalize font-weight-bold">{{
                data.item.month
              }}</span>
            </div>
          </template>

          <!-- Leads Columns -->
          <template #cell(leadQuantityOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.leadQuantityOkr }}</span>
            </div>
          </template>
          <template #cell(leadQuantityRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.leadQuantityRealized }}</span>
            </div>
          </template>
          <template #cell(leadQuantityPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.leadQuantityPercent }}%</span>
            </div>
          </template>

          <!-- Hot Leads Columns -->
          <template #cell(hotLeadQuantityOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.hotLeadQuantityOkr }}</span>
            </div>
          </template>
          <template #cell(hotLeadQuantityRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.hotLeadQuantityRealized }}</span>
            </div>
          </template>
          <template #cell(hotLeadQuantityPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.hotLeadQuantityPercent }}%</span>
            </div>
          </template>

          <!-- EA Columns -->
          <template #cell(eaQuantityOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.eaQuantityOkr }}</span>
            </div>
          </template>
          <template #cell(eaQuantityRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.eaQuantityRealized }}</span>
            </div>
          </template>
          <template #cell(eaQuantityPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.eaQuantityPercent }}%</span>
            </div>
          </template>

          <!-- EF Columns -->
          <template #cell(efQuantityOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.efQuantityOkr }}</span>
            </div>
          </template>
          <template #cell(efQuantityRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.efQuantityRealized }}</span>
            </div>
          </template>
          <template #cell(efQuantityPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.efQuantityPercent }}%</span>
            </div>
          </template>

          <!-- EP Columns -->
          <template #cell(epQuantityOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.epQuantityOkr }}</span>
            </div>
          </template>
          <template #cell(epQuantityRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.epQuantityRealized }}</span>
            </div>
          </template>
          <template #cell(epQuantityPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.epQuantityPercent }}%</span>
            </div>
          </template>

          <!-- EP Amount Columns -->
          <template #cell(epAmountOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.epAmountOkr | toCurrency }}</span>
            </div>
          </template>
          <template #cell(epAmountRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.epAmountRealized | toCurrency }}</span>
            </div>
          </template>
          <template #cell(epAmountPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.epAmountPercent }}%</span>
            </div>
          </template>

          <!-- MXA Columns -->
          <template #cell(mxaQuantityOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.mxaQuantityOkr }}</span>
            </div>
          </template>
          <template #cell(mxaQuantityRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.mxaQuantityRealized }}</span>
            </div>
          </template>
          <template #cell(mxaQuantityPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.mxaQuantityPercent }}%</span>
            </div>
          </template>

          <!-- PA Columns -->
          <template #cell(paQuantityOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.paQuantityOkr }}</span>
            </div>
          </template>
          <template #cell(paQuantityRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.paQuantityRealized }}</span>
            </div>
          </template>
          <template #cell(paQuantityPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.paQuantityPercent }}%</span>
            </div>
          </template>

          <!-- Subscriptions Columns -->
          <template #cell(subscriptionsAmountOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.subscriptionsAmountOkr | toCurrency }}</span>
            </div>
          </template>
          <template #cell(subscriptionsAmountRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.subscriptionsAmountRealized | toCurrency }}</span>
            </div>
          </template>
          <template #cell(subscriptionsAmountPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.subscriptionsAmountPercent }}%</span>
            </div>
          </template>

          <!-- Financial Solutions Columns -->
          <template #cell(financialSolutionsAmountOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.financialSolutionsAmountOkr | toCurrency }}</span>
            </div>
          </template>
          <template #cell(financialSolutionsAmountRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.financialSolutionsAmountRealized | toCurrency }}</span>
            </div>
          </template>
          <template #cell(financialSolutionsAmountPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.financialSolutionsAmountPercent }}%</span>
            </div>
          </template>

          <!-- Total Columns -->
          <template #cell(totalAmountOkr)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.totalAmountOkr | toCurrency }}</span>
            </div>
          </template>
          <template #cell(totalAmountRealized)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.totalAmountRealized | toCurrency }}</span>
            </div>
          </template>
          <template #cell(totalAmountPercent)="data">
            <div class="text-wrap text-center">
              <span class="align-text-top">{{ data.item.totalAmountPercent }}%</span>
            </div>
          </template>
        </b-table>
      </b-card>
    </div>
  </div>
</template>

<script>
import vSelect from "vue-select";
import {
  BCard,
  BCardBody,
  BCardHeader,
  BCardText,
  BRow,
  BCol,
  BFormGroup,
  BButton,
  BSpinner,
  BCollapse,
  BTable,
  BBadge,
  BTh,
  BTr,
} from "bootstrap-vue";
import FeatherIcon from "@core/components/feather-icon/FeatherIcon.vue";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import { mapGetters, mapActions } from "vuex";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import useAppConfig from "@core/app-config/useAppConfig";
import { detectSafariBrowser } from "@/mixins/detectSafariBrowser";
import _ from "lodash";

export default {
  name: "OkrReportsTable",
  components: {
    vSelect,
    FeatherIcon,
    BCard,
    BCardBody,
    BCardHeader,
    BCardText,
    BRow,
    BCol,
    BFormGroup,
    BButton,
    BSpinner,
    BCollapse,
    BTable,
    BBadge,
    BTh,
    BTr,
  },
  mixins: [detectSafariBrowser],
  data() {
    return {
      loading: {
        table: false,
        franchises: false,
        consultantList: false,
      },
      filters: {
        startQuarter: 1,
        startYear:    new Date().getFullYear(),
        endQuarter:   Math.ceil((new Date().getMonth() + 1) / 3),
        endYear:      new Date().getFullYear(),
        consultant:   undefined,
        franchises:   undefined,
      },
      tableColumns: [],
      stickyHeaderHeight: "400px",
    };
  },
  setup() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = String(now.getMonth() + 1).padStart(2, "0");
    const { skin } = useAppConfig();
    return { toast: useToast(), currentYear, currentMonth, skin };
  },
  watch: {
    processedReportData(newVal) {
      this.buildTableColumns();
      this.adjustStickyHeader(newVal);
    },
  },
  mounted() {
    this.buildTableColumns();
    this.fetchReports();
    this.loading.franchises = true;
    this.getFranchises()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar as franquias para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.franchises = false;
      });
    
    this.loading.consultantList = true;
    this.getConsultants()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao carregar os parceiros de negócio para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.consultantList = false;
      });
  },

  computed: {
    ...mapGetters({
      historicalPerformance: types.HISTORICAL_PERFORMANCE,
      franchises: sharedTypes.FRANCHISES,
      consultantList: sharedTypes.CONSULTANTS
    }),
    processedReportData() {
      const transformedData = this.transformResponse(this.historicalPerformance || {});
      const dataWithTotal = [...transformedData];
      
      if (transformedData.length > 0) {
        const total = this.calculateTotal(transformedData);
        dataWithTotal.push(total);
      }
      
      return dataWithTotal;
    },
    quarterOptions() {
      return [
        { value: 1, label: 'T1' },
        { value: 2, label: 'T2' },
        { value: 3, label: 'T3' },
        { value: 4, label: 'T4' },
      ]
    },

    yearOptions() {
      const years = []
      const max = new Date().getFullYear()
      for (let y = 2023; y <= max; y++) years.push(y)
      return years
    },

    averageRolePercentage() {
      return this.historicalPerformance?.average_role_percentage || 0.3;
    }
  },
  methods: {
    ...mapActions({
      getHistoricalPerformance: types.GET_HISTORICAL_PERFORMANCE,
      getFranchises: sharedTypes.GET_FRANCHISES,
      getConsultants: sharedTypes.GET_CONSULTANTS
    }),

    async fetchReports() {
      this.loading.table = true;
      try {
        const payload = {
          start_year:         this.filters.startYear,
          start_quarter:      this.filters.startQuarter,
          end_year:           this.filters.endYear,
          end_quarter:        this.filters.endQuarter,
          consultants:        this.filters.consultant,
          digital_franchises: this.filters.franchises,
        };

        await this.getHistoricalPerformance(payload);
      } catch (err) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Erro",
            text: "Ocorreu um erro ao buscar os relatórios. Tente novamente ou entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        this.loading.table = false;
      }
    },

    transformResponse(data) {
      // Combinar dados realizados e planejados para garantir que todos os períodos sejam incluídos
      const realizedData = data.realized_okr || [];
      const plannedData = data.planned_okr || [];
      
      // Criar um mapa de todos os períodos únicos (ano/trimestre)
      const allPeriods = new Set();

      realizedData.forEach(item => {
        allPeriods.add(`${item.year}-${item.quarter}`);
      });

      plannedData.forEach(item => {
        allPeriods.add(`${item.year}-${item.quarter}`);
      });

      // Converter para array e ordenar cronologicamente
      const sortedPeriods = Array.from(allPeriods).sort((a, b) => {
        const [yearA, quarterA] = a.split('-').map(Number);
        const [yearB, quarterB] = b.split('-').map(Number);

        if (yearA !== yearB) {
          return yearA - yearB;
        }
        return quarterA - quarterB;
      });

      // Processar cada período na ordem cronológica correta
      return sortedPeriods.map(periodKey => {
        const [year, quarter] = periodKey.split('-').map(Number);

        const realized = realizedData.find(r => r.year === year && r.quarter === quarter);
        const planned = plannedData.find(p => p.year === year && p.quarter === quarter);

        return {
          month: `T${quarter} ${year}`,
          sortYear: year,
          sortMonth: quarter,
          periodKey: periodKey,
          
          leadQuantityOkr: planned?.lead_quantity || 0,
          leadQuantityRealized: realized?.lead_quantity || 0,
          leadQuantityPercent: this.calcPercent(realized?.lead_quantity, planned?.lead_quantity),
          
          hotLeadQuantityOkr: planned?.hot_lead_quantity || 0,
          hotLeadQuantityRealized: realized?.hot_lead_quantity || 0,
          hotLeadQuantityPercent: this.calcPercent(realized?.hot_lead_quantity, planned?.hot_lead_quantity),
          
          eaQuantityOkr: planned?.ea_quantity || 0,
          eaQuantityRealized: realized?.ea_quantity || 0,
          eaQuantityPercent: this.calcPercent(realized?.ea_quantity, planned?.ea_quantity),
          
          efQuantityOkr: planned?.ef_quantity || 0,
          efQuantityRealized: realized?.ef_quantity || 0,
          efQuantityPercent: this.calcPercent(realized?.ef_quantity, planned?.ef_quantity),
          
          epQuantityOkr: planned?.ep_quantity || 0 ,
          epQuantityRealized: realized?.ep_quantity || 0,
          epQuantityPercent: this.calcPercent(realized?.ep_quantity, planned?.ep_quantity),
          
          epAmountOkr: planned?.ep_amount || 0,
          epAmountRealized: realized?.ep_amount || 0,
          epAmountPercent: this.calcPercent(realized?.ep_amount, planned?.ep_amount),
          
          mxaQuantityOkr: planned?.mxa_quantity || 0,
          mxaQuantityRealized: realized?.mxa_quantity || 0,
          mxaQuantityPercent: this.calcPercent(realized?.mxa_quantity, planned?.mxa_quantity),
          
          paQuantityOkr: planned?.pa_quantity || 0,
          paQuantityRealized: realized?.pa_quantity || 0,
          paQuantityPercent: this.calcPercent(realized?.pa_quantity, planned?.pa_quantity),
          
          subscriptionsAmountOkr: planned?.subscriptions_amount || 0,
          subscriptionsAmountRealized: realized?.subscriptions_amount || 0,
          subscriptionsAmountPercent: this.calcPercent(realized?.subscriptions_amount, planned?.subscriptions_amount),
          
          financialSolutionsAmountOkr: planned?.financial_solutions_amount || 0,
          financialSolutionsAmountRealized: realized?.financial_solutions_amount || 0,
          financialSolutionsAmountPercent: this.calcPercent(realized?.financial_solutions_amount, planned?.financial_solutions_amount),
          
          totalAmountOkr: planned?.okr_amount || 0,
          totalAmountRealized: realized?.total_amount || 0,
          totalAmountPercent: this.calcPercent(realized?.total_amount, planned?.okr_amount),
        };
      });
    },

    calcPercent(actual, planned) {
      if (planned && planned > 0 && actual >= 0){
        return Math.round((actual / planned) * 100);
      }
      return 0
    },

    calculateTotal(data) {
      return {
        month: "Total",
        
        leadQuantityOkr: _.sumBy(data, "leadQuantityOkr"),
        leadQuantityRealized: _.sumBy(data, "leadQuantityRealized"),
        leadQuantityPercent: this.calcPercent(
          _.sumBy(data, "leadQuantityRealized"), 
          _.sumBy(data, "leadQuantityOkr")
        ),
        
        hotLeadQuantityOkr: _.sumBy(data, "hotLeadQuantityOkr"),
        hotLeadQuantityRealized: _.sumBy(data, "hotLeadQuantityRealized"),
        hotLeadQuantityPercent: this.calcPercent(
          _.sumBy(data, "hotLeadQuantityRealized"), 
          _.sumBy(data, "hotLeadQuantityOkr")
        ),
        
        eaQuantityOkr: _.sumBy(data, "eaQuantityOkr"),
        eaQuantityRealized: _.sumBy(data, "eaQuantityRealized"),
        eaQuantityPercent: this.calcPercent(
          _.sumBy(data, "eaQuantityRealized"), 
          _.sumBy(data, "eaQuantityOkr")
        ),
        
        efQuantityOkr: _.sumBy(data, "efQuantityOkr"),
        efQuantityRealized: _.sumBy(data, "efQuantityRealized"),
        efQuantityPercent: this.calcPercent(
          _.sumBy(data, "efQuantityRealized"), 
          _.sumBy(data, "efQuantityOkr")
        ),
        
        epQuantityOkr: _.sumBy(data, "epQuantityOkr"),
        epQuantityRealized: _.sumBy(data, "epQuantityRealized"),
        epQuantityPercent: this.calcPercent(
          _.sumBy(data, "epQuantityRealized"), 
          _.sumBy(data, "epQuantityOkr")
        ),
        
        epAmountOkr: _.sumBy(data, "epAmountOkr"),
        epAmountRealized: _.sumBy(data, "epAmountRealized"),
        epAmountPercent: this.calcPercent(
          _.sumBy(data, "epAmountRealized"), 
          _.sumBy(data, "epAmountOkr")
        ),
        
        mxaQuantityOkr: _.sumBy(data, "mxaQuantityOkr"),
        mxaQuantityRealized: _.sumBy(data, "mxaQuantityRealized"),
        mxaQuantityPercent: this.calcPercent(
          _.sumBy(data, "mxaQuantityRealized"), 
          _.sumBy(data, "mxaQuantityOkr")
        ),
        
        paQuantityOkr: _.sumBy(data, "paQuantityOkr"),
        paQuantityRealized: _.sumBy(data, "paQuantityRealized"),
        paQuantityPercent: this.calcPercent(
          _.sumBy(data, "paQuantityRealized"), 
          _.sumBy(data, "paQuantityOkr")
        ),
        
        subscriptionsAmountOkr: _.sumBy(data, "subscriptionsAmountOkr"),
        subscriptionsAmountRealized: _.sumBy(data, "subscriptionsAmountRealized"),
        subscriptionsAmountPercent: this.calcPercent(
          _.sumBy(data, "subscriptionsAmountRealized"), 
          _.sumBy(data, "subscriptionsAmountOkr")
        ),
        
        financialSolutionsAmountOkr: _.sumBy(data, "financialSolutionsAmountOkr"),
        financialSolutionsAmountRealized: _.sumBy(data, "financialSolutionsAmountRealized"),
        financialSolutionsAmountPercent: this.calcPercent(
          _.sumBy(data, "financialSolutionsAmountRealized"), 
          _.sumBy(data, "financialSolutionsAmountOkr")
        ),
        
        totalAmountOkr: _.sumBy(data, "totalAmountOkr"),
        totalAmountRealized: _.sumBy(data, "totalAmountRealized"),
        totalAmountPercent: this.calcPercent(
          _.sumBy(data, "totalAmountRealized"), 
          _.sumBy(data, "totalAmountOkr")
        ),
      };
    },

    rowClass(item, type) {
      if (!item || type !== "row") return "";
      if (item.month === "Total") {
        if (this.skin === "dark") {
          return "total-dark-color";
        } else if (this.skin === "light") {
          return "table-primary";
        }
      }
      return "";
    },

    buildTableColumns() {
      this.tableColumns = [
        { key: "month", label: "Mês", stickyColumn: true, thClass: ["header-spacing"] },
        
        // Leads
        { key: "leadQuantityOkr", label: "OKR", thClass: ["bg-danger", "text-white"], sortable: true },
        { key: "leadQuantityRealized", label: "Real.", thClass: ["bg-danger", "bg-lighten-3"], sortable: true },
        { key: "leadQuantityPercent", label: "%", thClass: ["header-spacing", "bg-danger", "bg-lighten-5"], sortable: true },
        
        // Hot Leads
        { key: "hotLeadQuantityOkr", label: "OKR", thClass: ["bg-danger", "bg-lighten-2"], sortable: true },
        { key: "hotLeadQuantityRealized", label: "Real.", thClass: ["bg-danger", "bg-lighten-4"], sortable: true },
        { key: "hotLeadQuantityPercent", label: "%", thClass: ["header-spacing", "bg-danger", "bg-lighten-6"], sortable: true },
        
        // EA
        { key: "eaQuantityOkr", label: "OKR", thClass: ["bg-info"], sortable: true },
        { key: "eaQuantityRealized", label: "Real.", thClass: ["bg-info", "bg-lighten-3"], sortable: true },
        { key: "eaQuantityPercent", label: "%", thClass: ["header-spacing", "bg-info", "bg-lighten-5"], sortable: true },
        
        // EF
        { key: "efQuantityOkr", label: "OKR", thClass: ["bg-info", "bg-lighten-2"], sortable: true },
        { key: "efQuantityRealized", label: "Real.", thClass: ["bg-info", "bg-lighten-4"], sortable: true },
        { key: "efQuantityPercent", label: "%", thClass: ["header-spacing", "bg-info", "bg-lighten-6"], sortable: true },
        
        // EP
        { key: "epQuantityOkr", label: "OKR", thClass: ["bg-info", "bg-lighten-1"], sortable: true },
        { key: "epQuantityRealized", label: "Real.", thClass: ["bg-info", "bg-lighten-3"], sortable: true },
        { key: "epQuantityPercent", label: "%", thClass: ["header-spacing", "bg-info", "bg-lighten-5"], sortable: true },
        
        // EP Amount
        { key: "epAmountOkr", label: "OKR", thClass: ["bg-green-2", "bg-lighten-5"], tdClass: ["pl-0", "pr-4"], sortable: true },
        { key: "epAmountRealized", label: "Real.", thClass: ["bg-green-2", "bg-lighten-2"], tdClass: ["pl-0", "pr-4"], sortable: true },
        { key: "epAmountPercent", label: "%", thClass: ["header-spacing", "bg-green-2", "bg-lighten-5"], sortable: true },
        
        // MXA
        { key: "mxaQuantityOkr", label: "OKR", thClass: ["bg-blue", "bg-lighten-5", "text-white"], sortable: true },
        { key: "mxaQuantityRealized", label: "Real.", thClass: ["bg-blue", "bg-lighten-2", "text-white"], sortable: true },
        { key: "mxaQuantityPercent", label: "%", thClass: ["header-spacing", "bg-blue", "bg-lighten-5", "text-white"], sortable: true },
        
        // PA
        { key: "paQuantityOkr", label: "OKR", thClass: ["bg-primary", "bg-lighten-2"], sortable: true },
        { key: "paQuantityRealized", label: "Real.", thClass: ["bg-primary", "bg-lighten-4"], sortable: true },
        { key: "paQuantityPercent", label: "%", thClass: ["header-spacing", "bg-primary", "bg-lighten-6"], sortable: true },
        
        // Subscriptions
        { key: "subscriptionsAmountOkr", label: "OKR", thClass: ["bg-secondary", "bg-lighten-1"], tdClass: ["pl-0", "pr-4"], sortable: true },
        { key: "subscriptionsAmountRealized", label: "Real.", thClass: ["bg-secondary", "bg-lighten-3"], tdClass: ["pl-0", "pr-4"], sortable: true },
        { key: "subscriptionsAmountPercent", label: "%", thClass: ["header-spacing", "bg-secondary", "bg-lighten-5"], sortable: true },
        
        // Financial Solutions
        { key: "financialSolutionsAmountOkr", label: "OKR", thClass: ["bg-green-2", "bg-lighten-2"], tdClass: ["pl-0", "pr-4"], sortable: true },
        { key: "financialSolutionsAmountRealized", label: "Real.", thClass: ["bg-green-2", "bg-lighten-5"], tdClass: ["pl-0", "pr-4"], sortable: true },
        { key: "financialSolutionsAmountPercent", label: "%", thClass: ["header-spacing", "bg-green-2", "bg-lighten-2"], sortable: true },
        
        // Total
        { key: "totalAmountOkr", label: "OKR", thClass: ["bg-success", "bg-lighten-2"], tdClass: ["pl-0", "pr-4"], sortable: true },
        { key: "totalAmountRealized", label: "Real.", thClass: ["bg-success", "bg-lighten-4"], tdClass: ["pl-0", "pr-4"], sortable: true },
        { key: "totalAmountPercent", label: "%", thClass: ["bg-success", "bg-lighten-6"], sortable: true },
      ];
    },

    adjustStickyHeader(data) {
      let height = 400;
      if (data && data.length > 5 && data.length <= 10) {
        height = 500;
      } else if (data && data.length > 10) {
        height = 700;
      }
      this.stickyHeaderHeight = `${height}px`;
    },
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.popover.b-popover {
  max-width: 500px !important;
  width: max-content !important;
}

::v-deep .table {
  thead {
    position: sticky !important;
    top: 0 !important;
    z-index: 3 !important;
  }
}

.card .table tbody tr:last-child td:first-child {
  border-bottom-left-radius: 0 !important;
}
</style>

<style lang="scss">
.header-spacing {
  border-right: 15px solid #ebe9f1;
}

.column-spacing {
  border-right: 10px solid #ebe9f1;
}

th {
  text-align: center;
}

[aria-sort="none"] {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='14px' height='26px' viewBox='0 0 14 26' version='1.1'%3E%3Ctitle%3Earrows%3C/title%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' stroke-linecap='round' stroke-linejoin='round'%3E%3Cg id='Artboard' transform='translate(-257.000000, -561.000000)' stroke='%236e6b7b' stroke-width='2'%3E%3Cg id='arrows' transform='translate(258.000000, 562.000000)'%3E%3Cg id='chevron-down' transform='translate(0.000000, 18.000000)'%3E%3Cpolyline id='Path' points='0 0 6 6 12 0'/%3E%3C/g%3E%3Cg id='chevron-up'%3E%3Cpolyline id='Path' points='12 6 6 0 0 6'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E") !important;
}

[aria-sort="ascending"] {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='14px' height='26px' viewBox='0 0 14 26' version='1.1'%3E%3Ctitle%3Eactive-up%3C/title%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' stroke-linecap='round' stroke-linejoin='round'%3E%3Cg id='Artboard' transform='translate(-197.000000, -561.000000)' stroke-width='2'%3E%3Cg id='active-up' transform='translate(198.000000, 562.000000)'%3E%3Cg id='chevron-down' transform='translate(0.000000, 18.000000)' stroke='%236e6b7b'%3E%3Cpolyline id='Path' points='0 0 6 6 12 0'/%3E%3C/g%3E%3Cg id='chevron-up' stroke='%23FFFFFF'%3E%3Cpolyline id='Path' points='12 6 6 0 0 6'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E") !important;
}

[aria-sort="descending"] {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' width='14px' height='26px' viewBox='0 0 14 26' version='1.1'%3E%3Ctitle%3Eactive-up%3C/title%3E%3Cg id='Page-1' stroke='none' stroke-width='1' fill='none' fill-rule='evenodd' stroke-linecap='round' stroke-linejoin='round'%3E%3Cg id='Artboard' transform='translate(-197.000000, -561.000000)' stroke-width='2'%3E%3Cg id='active-up' transform='translate(198.000000, 562.000000)'%3E%3Cg id='chevron-down' transform='translate(0.000000, 18.000000)' stroke='%23FFFFFF'%3E%3Cpolyline id='Path' points='0 0 6 6 12 0'/%3E%3C/g%3E%3Cg id='chevron-up' stroke='%236e6b7b'%3E%3Cpolyline id='Path' points='12 6 6 0 0 6'/%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/g%3E%3C/svg%3E") !important;
}

.scroll-fixed {
  overflow-x: scroll !important;
}

.total-dark-color {
  background-color: #ff7f50;

  &:hover {
    background-color: rgba(255, 127, 80, 0.8) !important;
  }
}
</style>
